import { ArrowLeft, MessageSquare, Sparkles, TrendingDown, TrendingUp, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  eyebrow: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

export function PageHeader({ eyebrow, title, subtitle, actions, className }: PageHeaderProps) {
  return (
    <header
      className={cn(
        "mb-5 flex flex-col gap-3 @3xl:flex-row @3xl:items-start @3xl:justify-between @3xl:gap-4",
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {eyebrow}
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight @sm:text-3xl">{title}</h1>
        {subtitle && <div className="mt-1 text-sm text-muted-foreground">{subtitle}</div>}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 @3xl:shrink-0">
          {actions}
        </div>
      )}
    </header>
  );
}

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  trend?: "up" | "down";
  /** Tags the card with a small "Demo" badge so users know the value isn't wired to live data. */
  placeholder?: boolean;
  /** When provided, renders an "Ask agent" pill that opens the chat and asks about this card. */
  onAskAgent?: () => void;
}

export function StatCard({ label, value, sub, trend, placeholder, onAskAgent }: StatCardProps) {
  return (
    <div
      className={cn(
        "group relative rounded-xl border bg-card/60 p-4 backdrop-blur-sm",
        placeholder && "border-dashed",
      )}
    >
      {placeholder && (
        <span className="absolute right-3 top-3 inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
          Demo
        </span>
      )}
      {onAskAgent && !placeholder && (
        <button
          type="button"
          onClick={onAskAgent}
          className="absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-full border border-accent-blue/30 bg-accent-blue/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent-blue transition hover:bg-accent-blue/20"
          aria-label={`Ask agent about ${label}`}
        >
          <MessageSquare className="size-2.5" />
          Ask agent
        </button>
      )}
      <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight">{value}</p>
      {sub !== undefined && (
        <div className="mt-0.5 flex items-center gap-1 text-xs">
          {trend === "up" && <TrendingUp className="size-3 text-emerald-600" />}
          {trend === "down" && <TrendingDown className="size-3 text-red-600" />}
          <span
            className={cn(
              trend === "up"
                ? "text-emerald-600"
                : trend === "down"
                  ? "text-red-600"
                  : "text-muted-foreground",
            )}
          >
            {sub}
          </span>
        </div>
      )}
    </div>
  );
}

interface AgentSuggestionAction {
  label: string;
  icon?: LucideIcon;
  primary?: boolean;
  onClick?: () => void;
}

interface AgentSuggestionCardProps {
  title: string;
  body: React.ReactNode;
  actions?: AgentSuggestionAction[];
  className?: string;
  /** Tags the card with a small "Demo" badge so users know the content isn't wired to live data. */
  placeholder?: boolean;
}

export function AgentSuggestionCard({
  title,
  body,
  actions,
  className,
  placeholder,
}: AgentSuggestionCardProps) {
  return (
    <div
      className={cn(
        "relative rounded-xl border border-accent-blue/30 bg-accent-blue/5 p-4",
        placeholder && "border-dashed",
        className,
      )}
    >
      {placeholder && (
        <span className="absolute right-3 top-3 inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
          Demo
        </span>
      )}
      <div className="flex items-start gap-3">
        <div className="grid size-8 shrink-0 place-items-center rounded-lg border border-accent-blue/25 bg-accent-blue/10 text-accent-blue">
          <Sparkles className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-accent-blue">
            Agent suggests
          </p>
          <p className="mt-1 text-sm font-semibold">{title}</p>
          <div className="mt-1 text-xs text-muted-foreground">{body}</div>
          {actions && actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {actions.map((a) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.label}
                    type="button"
                    onClick={a.onClick}
                    className={cn(
                      "inline-flex h-7 items-center gap-1 rounded-md border px-2.5 text-xs font-medium transition",
                      a.primary
                        ? "border-accent-blue/40 bg-accent-blue/10 text-accent-blue hover:bg-accent-blue/20"
                        : "border-border text-foreground hover:bg-muted",
                    )}
                  >
                    {Icon && <Icon className="size-3" />}
                    {a.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ListCardItem {
  key: string;
  left: React.ReactNode;
  right: React.ReactNode;
  tone?: "red" | "green";
  onClick?: () => void;
}

export function ListCard({
  title,
  items,
  placeholder,
}: {
  title: string;
  items: ListCardItem[];
  /** Tags the card with a small "Demo" badge so users know the items aren't wired to live data. */
  placeholder?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative rounded-xl border bg-card/60 p-4 backdrop-blur-sm",
        placeholder && "border-dashed",
      )}
    >
      {placeholder && (
        <span className="absolute right-3 top-3 inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
          Demo
        </span>
      )}
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {title}
      </p>
      <div className="flex flex-col">
        {items.map((it) => {
          const interactive = !!it.onClick;
          const Tag = interactive ? "button" : "div";
          return (
            <Tag
              key={it.key}
              type={interactive ? "button" : undefined}
              onClick={it.onClick}
              className={cn(
                "flex items-center justify-between border-b py-2 text-sm last:border-b-0",
                interactive && "-mx-2 cursor-pointer rounded px-2 text-left transition hover:bg-muted",
              )}
            >
              <span className="truncate">{it.left}</span>
              <span
                className={cn(
                  it.tone === "red"
                    ? "font-medium text-red-600"
                    : it.tone === "green"
                      ? "font-medium text-emerald-600"
                      : "text-muted-foreground",
                )}
              >
                {it.right}
              </span>
            </Tag>
          );
        })}
      </div>
    </div>
  );
}

export function BackLink({ onClick, children }: { onClick?: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" />
      {children}
    </button>
  );
}

/** One width for every page so navigation never shifts the layout; `narrow` is only for single-column forms. */
export function PageContainer({
  children,
  size = "default",
}: {
  children: React.ReactNode;
  size?: "default" | "narrow";
}) {
  return (
    <div className={cn("mx-auto w-full p-4 sm:p-6", size === "narrow" ? "max-w-2xl" : "max-w-6xl")}>
      {children}
    </div>
  );
}
